import { useCallback, useState } from 'react'

// by default if parseData is not passed just return the data as it is
const defaultParseData = (data) => data

// by default if parseError is not passed set error as a boolean
const defaultParseError = (error) => !!error

const callApi = async ({
  api,
  setData,
  setSuccess,
  setError,
  setLoading,
  parseData,
  parseError,
  params,
  initialData,
  options = {},
}) => {
  const { clearDataBeforeRequesting = true } = options

  setLoading(true)
  setError(null)
  setSuccess(false)
  if (clearDataBeforeRequesting) {
    setData(initialData ?? null)
  }

  let response = null
  let error = null
  let data = null
  let success = false
  try {
    response = await api(params)

    data = parseData(response?.data)
    setData(data)

    success = true
    setSuccess(success)
  } catch (err) {
    console.error(err)
    error = parseError(err)
    setError(error)
    setData(initialData ?? null)
  }

  setLoading(false)

  return {
    response,
    error,
    data,
    success,
  }
}

/**
 * NOTE: all parameters passed to useApiControls must be memoized! Use: useMemo, useCallback or pass a constant declared outside of the component/hook
 * Otherwise callApi will be re-done on every render and it will mess with any useEffect calling the api
 */
const useApiControls = ({ api, initialData, parseData = defaultParseData, parseError = defaultParseError }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [data, setData] = useState(initialData ?? null)
  const [success, setSuccess] = useState(false)

  const callApiCb = useCallback(
    (params, options) =>
      callApi({
        api,
        setData,
        setSuccess,
        setError,
        setLoading,
        parseData,
        parseError,
        params,
        initialData,
        options,
      }),
    [api, parseData, parseError, initialData],
  )

  const reset = useCallback(() => {
    setLoading(false)
    setError(false)
    setData(initialData ?? null)
    setSuccess(false)
  }, [initialData])

  return {
    loading,
    error,
    data,
    success,
    reset,
    callApi: callApiCb,
  }
}

export default useApiControls
